<!-- Communication Display -->
<div
  #messagesContainer
  class="relative min-h-0 flex-1 overflow-y-auto px-4 py-2"
>
  <div class="sticky top-2 z-10 flex justify-end">
    <pr-referral-communication-type-filter
      [selectedTypes]="selectedCommunicationTypes()"
      [showNotes]="canAddNotes()"
      [showInteractions]="showInteractions()"
      (selectionChange)="onCommunicationTypeFilterChange($event)"
    />
  </div>
  <div class="flex flex-col gap-3">
    @for (group of communicationItemsByDate(); track group.date) {
      <!-- Date header -->
      <div class="my-2 flex justify-center">
        <div class="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-500">
          {{ group.date | moment | amDateFormat: dateFormat }}
        </div>
      </div>

      <!-- Communication items for this date -->
      <div class="flex flex-col gap-4">
        @for (item of group.items; track $index) {
          @switch (item.type) {
            @case (communicationItemType.Message) {
              <div
                class="w-full min-w-[300px] rounded-lg border border-solid border-gray-200 p-3"
              >
                <div class="mb-2 flex items-center gap-3">
                  <pt-user-icon
                    class="shrink-0"
                    [name]="item.item.actor.name"
                    [diameter]="36"
                    [backgroundColour]="
                      item.direction === communicationDirection.Inbound
                        ? incomingUserIconBackgroundColour
                        : undefined
                    "
                  />
                  <div class="flex flex-1 items-center gap-4">
                    <div class="font-bold">{{ item.item.actor.name }}</div>
                    <div class="text-xs text-gray-500">
                      {{
                        item.item.createdAt | moment | amDateFormat: timeFormat
                      }}
                    </div>
                  </div>
                </div>

                <pr-content-view
                  [content]="(item | map: asMessage : this).content"
                />
              </div>
            }

            @case (communicationItemType.Interaction) {
              <div class="flex gap-2">
                <div
                  class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#3CB796]"
                >
                  <mat-icon class="icon-sm text-white">
                    {{
                      item
                        | map: asInteraction : this
                        | map: getInteractionIcon : this
                    }}
                  </mat-icon>
                </div>
                <div class="flex flex-col text-xs">
                  <div class="flex items-center gap-2">
                    <span class="text-xs font-bold">
                      {{ item.item.actor.name }}
                    </span>
                    <span class="text-xs text-gray-500">
                      {{
                        item.item.createdAt | moment | amDateFormat: timeFormat
                      }}
                    </span>
                  </div>
                  <pr-content-view
                    class="interaction"
                    [content]="(item | map: asInteraction : this).content"
                  />
                </div>
              </div>
            }

            @case (communicationItemType.Media) {
              <div
                class="w-full min-w-[300px] rounded-lg border border-solid border-gray-200 p-3"
              >
                <div class="mb-2 flex items-center gap-3">
                  <pt-user-icon
                    class="shrink-0"
                    [name]="item.item.actor.name"
                    [diameter]="36"
                    [backgroundColour]="
                      item.direction === communicationDirection.Inbound
                        ? incomingUserIconBackgroundColour
                        : undefined
                    "
                  />
                  <div class="flex flex-1 items-center gap-4">
                    <div class="font-bold">{{ item.item.actor.name }}</div>
                    <div class="text-xs text-gray-500">
                      {{
                        item.item.createdAt | moment | amDateFormat: timeFormat
                      }}
                    </div>
                  </div>
                </div>

                @if (item.mediaGroup && item.mediaGroup.length > 1) {
                  <div class="mt-2 flex flex-wrap gap-2">
                    @for (
                      mediaItem of item.mediaGroup;
                      track mediaItem.ref.path
                    ) {
                      <div
                        class="group relative h-40 w-64 cursor-pointer overflow-hidden rounded transition-shadow hover:shadow-md"
                        (click)="viewMedia(item)"
                      >
                        <pr-media-tile
                          class="h-full w-full"
                          [media]="mediaItem"
                          [showDate]="false"
                          [showImageName]="false"
                          [showFileName]="true"
                        />
                        <div class="invisible group-hover:visible">
                          <pr-referral-media-menu
                            [media]="[mediaItem]"
                            [referral]="referral()"
                            [patient]="patient()"
                            [currentUser]="currentUser()"
                            [canManagePatientMedia]="canManagePatientMedia()"
                          />
                        </div>
                      </div>
                    }
                  </div>
                } @else {
                  <div
                    class="group relative mt-2 h-40 w-64 cursor-pointer overflow-hidden rounded transition-shadow hover:shadow-md"
                    (click)="viewMedia(item)"
                  >
                    <pr-media-tile
                      class="h-full w-full"
                      [media]="item | map: asMedia : this"
                      [showDate]="false"
                      [showImageName]="false"
                      [showFileName]="true"
                    />
                    <div class="invisible group-hover:visible">
                      <pr-referral-media-menu
                        [media]="[item | map: asMedia : this]"
                        [referral]="referral()"
                        [patient]="patient()"
                        [currentUser]="currentUser()"
                        [canManagePatientMedia]="canManagePatientMedia()"
                      />
                    </div>
                  </div>
                }
              </div>
            }

            @case (communicationItemType.ClinicalNote) {
              <div
                class="w-full min-w-[300px] rounded-lg border border-solid border-gray-200 p-3"
              >
                <div class="mb-2 flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <pt-user-icon
                      class="shrink-0"
                      [name]="(item | map: asClinicalNote : this).actor.name"
                      [diameter]="36"
                      [backgroundColour]="
                        item.direction === communicationDirection.Inbound
                          ? incomingUserIconBackgroundColour
                          : undefined
                      "
                    />
                    <div class="flex flex-1 items-center gap-4">
                      <div class="font-bold">
                        {{ (item | map: asClinicalNote : this).actor.name }}
                      </div>
                      <div class="text-xs text-gray-500">
                        {{
                          (item | map: asClinicalNote : this).createdAt
                            | moment
                            | amDateFormat: timeFormat
                        }}
                      </div>
                    </div>
                  </div>
                  <div
                    class="rounded-full bg-gray-200 px-2 py-1 text-xs font-semibold text-gray-500"
                  >
                    <mat-icon class="icon-sm align-middle">note_add</mat-icon>
                    Clinical Note
                  </div>
                </div>

                <div class="mt-2">
                  <pr-referral-clinical-note-display
                    [clinicalNote]="
                      (item | map: asClinicalNote : this).clinicalNote
                    "
                  />
                </div>
              </div>
            }

            @case (communicationItemType.Note) {
              @let note = asNote(item);
              <div
                class="group relative w-full min-w-[300px] rounded-lg bg-amber-50 p-3"
              >
                <!-- Hover menu for edit/delete -->
                @if (!isEditingNote(note)) {
                  <div
                    class="invisible absolute right-2 top-2 z-10 group-hover:visible"
                  >
                    <button
                      mat-icon-button
                      class="!bg-white !shadow-md"
                      [matMenuTriggerFor]="noteMenu"
                      (click)="$event.stopPropagation()"
                    >
                      <mat-icon>more_vert</mat-icon>
                    </button>

                    <mat-menu #noteMenu="matMenu">
                      <button mat-menu-item (click)="startEditNote(note)">
                        <mat-icon>edit</mat-icon>
                        <span>Edit</span>
                      </button>
                      <button mat-menu-item (click)="deleteNote(note)">
                        <mat-icon color="warn">delete</mat-icon>
                        <span class="text-red-600">Delete</span>
                      </button>
                    </mat-menu>
                  </div>
                }

                <div class="mb-2 flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <pt-user-icon
                      class="shrink-0"
                      [name]="note.actor.name"
                      [diameter]="36"
                    />
                    <div class="flex flex-1 items-center gap-4">
                      <div class="font-bold">
                        {{ note.actor.name }}
                      </div>
                      <div class="text-xs text-gray-500">
                        {{ note.createdAt | moment | amDateFormat: timeFormat }}
                      </div>
                    </div>
                  </div>
                  <div
                    class="rounded-full bg-amber-200 px-2 py-1 text-xs font-semibold text-gray-500"
                    matTooltip="Only visible to your team"
                  >
                    <mat-icon class="icon-sm align-middle"
                      >text_snippet</mat-icon
                    >
                    Note
                  </div>
                </div>

                @if (isEditingNote(note)) {
                  <div class="mt-2">
                    <mat-form-field
                      subscriptSizing="dynamic"
                      class="w-full bg-white"
                    >
                      <pr-content-editor
                        class="w-full"
                        [formControl]="editNoteCtrl"
                      />
                    </mat-form-field>
                    <div class="mt-2 flex justify-end gap-2">
                      <button mat-button (click)="cancelEditNote()">
                        Cancel
                      </button>
                      <button
                        mat-flat-button
                        color="primary"
                        (click)="saveEditNote(note)"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                } @else {
                  <div class="mt-2">
                    <pr-content-view [content]="note.content" />
                  </div>
                }
              </div>
            }
          }
        }
      </div>
    } @empty {
      <div class="flex h-full w-full items-center justify-center">
        <pr-empty-state
          image="message"
          title="communication available"
          imageClasses="w-32"
          titleClasses="!text-sm"
          [descriptionEnabled]="false"
        />
      </div>
    }
  </div>
</div>

<!-- Message Input -->
<div
  class="mt-4 flex max-h-[80%] flex-col gap-2 overflow-y-auto overflow-x-hidden"
>
  <div class="px-4 py-2">
    <mat-form-field subscriptSizing="dynamic" class="w-full">
      <mat-label>Type a message</mat-label>
      <pr-content-editor
        class="w-full"
        [formControl]="messageCtrl"
        [required]="true"
        [menuItems]="menuItems().length ? menuItems() : undefined"
      />
    </mat-form-field>
  </div>

  <div class="flex w-full items-center gap-2 px-4">
    <ng-content select="attach-file" />

    <span class="flex-1"></span>

    @if (pdfService.generatingPdf()) {
      <div class="flex items-center gap-2 text-xs text-gray-500">
        <mat-spinner diameter="16" />
        <span>Generating PDF...</span>
      </div>
    }

    <mat-form-field subscriptSizing="dynamic">
      <mat-select
        [value]="selectedAddMode()"
        (selectionChange)="selectedAddMode.set($event.value)"
      >
        <mat-select-trigger>
          @for (option of addModeOptions(); track option.value) {
            @if (option.value === selectedAddMode()) {
              <div class="flex items-center">
                <mat-icon class="icon-sm mr-2">{{ option.icon }}</mat-icon>
                <span>{{ option.label }}</span>
              </div>
            }
          }
        </mat-select-trigger>
        @for (option of addModeOptions(); track option.value) {
          <mat-option matTooltipPosition="left" [value]="option.value" [matTooltip]="option.tooltip">
            <mat-icon class="icon-sm mr-2">{{ option.icon }}</mat-icon>
            <span>{{ option.label }}</span>
          </mat-option>
        }
      </mat-select>
    </mat-form-field>

    <button
      type="button"
      mat-flat-button
      color="primary"
      matTooltip="Add"
      [disabled]="
        isDisabled() ||
        pdfService.generatingPdf() ||
        referralMediaService.uploadInProgress()
      "
      (click)="executeAddAction()"
    >
      <mat-icon>add</mat-icon>
      Add
    </button>
  </div>
</div>
